import React, {  useState } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import { FaGlasses } from 'react-icons/fa';
import moment from 'moment';
import { useSelector } from "react-redux";
import {
  DashboardStatus,
  dashboardStatusHexColors,
  getDashboardStatusLabel
} from "../../constants/integrationTypes";
import { useJwt } from "../../helpers/common";
import CustomTooltip from '../../components/common/CustomTooltip';
import { useResourceInfo } from '../../hooks/useResourceInfo';
import { getEntityTypeDisplay } from '../../utils/entityDisplayUtils';

interface EntityViewTooltipProps {
  projectData?: any;
  taskData?: any;
  isSimulation?: boolean;
  tooltipType?: 'project' | 'task';
  isTemplate?: boolean;
}

export const EntityViewTooltip: React.FC<EntityViewTooltipProps> = ({
  projectData,
  taskData,
  isSimulation = false,
  tooltipType = 'project',
  isTemplate = false,
}) => {

  const jwtData: any = useJwt();
  const isAdmin = typeof jwtData?.org_role === 'string' && jwtData?.org_role?.toLowerCase() === 'admin';
  const { show_cost } = useSelector((state: any) => state.organization.orgDetails);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const { getResourceName } = useResourceInfo();

  const formatTooltipText = (text: string) => {
    if (!text) return "";
    return text
      .split(/[\s_]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey(null), 1500);
    } catch (err) {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopiedKey(key);
        window.setTimeout(() => setCopiedKey(null), 1500);
      } catch (_) { }
    }
  };

  const CopyIcon = ({ isCopied = false }: { isCopied?: boolean }) => (
    isCopied ? (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ) : (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    )
  );

  if (tooltipType === 'project' && projectData) {
    // Legacy support for StrategyMapSidePanelItem which passes projectData directly but might not have tooltipType prop set or assumes project structure
    // If the component is used with just projectData, we treat it as project type.
    
    // Normalize data access
    const {
      id: entity_id,
      sim_data,
      progress,
    } = projectData;


    // Use project_name if available (from ProjectItem), else name (StrategyMapSidePanelItem)
    const entity_name = projectData.project_name || projectData.name;
    const projectId = projectData.project_id || entity_id;

    // Dates
    const pStart = isSimulation ? (sim_data?.start_date || projectData?.start_date) : projectData?.start_date;
    const pEnd = isSimulation ? (sim_data?.end_date || projectData?.end_date) : projectData?.end_date;
    
    const stage = projectData?.stage;
    const progressVal = Math.floor(Number(progress ?? projectData?.complete ?? 0));
    
    // Cost
    const showCostVal = !!isSimulation && isAdmin && show_cost && ((Number(projectData?.project_cost) || 0) > 0 || (Number(sim_data?.project_cost) || 0) > 0);

    // Status
    const status = projectData.status; 
    const currentStatus = isSimulation ? (sim_data?.status || status) : status;
    const statusKey = currentStatus?.toLowerCase() || '';
    const statusLabel = getDashboardStatusLabel(statusKey);
    const statusHexColor = dashboardStatusHexColors[statusKey] || '#a3a3a3';
    
    // Risk
    const showRiskTriangle = [DashboardStatus.AT_RISK, DashboardStatus.DELAYED, DashboardStatus.UNALLOCATED, DashboardStatus.BLOCKED].includes(statusKey as DashboardStatus);

    // Integration Status for ProjectItem (if available)
    const integrationStatus = projectData?.integration_data?.integration_status;
    const showIntegrationStatus = integrationStatus && integrationStatus.toLowerCase() !== statusKey;

    const pEntityInfo = getEntityTypeDisplay(projectData?.entity_type || 'work_unit', projectData?.type || 'project');
    const PIcon = pEntityInfo.icon;

    return (
      <div className="p-3 space-y-1 text-slate-700 dark:text-slate-200 flex flex-col items-start min-w-[220px]">
        {!isTemplate && showRiskTriangle && (
          <div className="text-[11px] text-red-600 dark:text-red-400 inline-flex items-center gap-1">
            <FiAlertTriangle className="text-red-500" />
            {`This is contributing to risk`}
          </div>
        )}

        <div className="flex flex-col w-full gap-2 pb-2 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2 w-full group">
            <span
              className="text-sm font-bold text-start  cursor-pointer hover:text-slate-800 dark:hover:text-slate-100 w-fit"
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(entity_name, "Project Name");
              }}
            >
              {entity_name}
            </span>

            <button
              className={`p-1 transition-colors opacity-0 group-hover:opacity-100 ${copiedKey === "Project Name" ? "text-green-500" : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"}`}
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(entity_name, "Project Name");
              }}
            >
              <CopyIcon isCopied={copiedKey === "Project Name"} />
            </button>
          </div>
          <span
            className={` ${pEntityInfo.chipClass} px-2 py-1 flex items-center justify-center bg-gray-200 rounded-full w-fit gap-2`}
          >
            {PIcon && <PIcon className={`w-3 h-3 ${pEntityInfo.iconClass}`} />}
            <span className={`${pEntityInfo.labelClass} text-[10px]`}>
              {pEntityInfo.label}
              {projectData?.integration_data?.integration_type ? (
                <span className="uppercase">
                  {" "}
                  (
                  {formatTooltipText(
                    projectData.integration_data.integration_type,
                  )}
                  )
                </span>
              ) : (
                ""
              )}
            </span>
          </span>
        </div>

        <div className="flex items-center w-full ml-[-5px] gap-1 flex-wrap pb-2">
          {!isTemplate && (
            <span className="text-[11px] text-slate-500 dark:text-slate-400 uppercase tracking-wide px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800">
              Status:{" "}
              <b style={{ color: statusHexColor }}>{statusLabel || "-"}</b>
            </span>
          )}
          <span className="text-[11px] text-slate-500 dark:text-slate-400 uppercase tracking-wide px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800">
            Stage: {stage || "-"}
          </span>
          {showIntegrationStatus && (
            <span className="text-[11px] text-slate-500 dark:text-slate-400 uppercase tracking-wide px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800">
              Integration Status:{" "}
              {integrationStatus.charAt(0).toUpperCase() +
                integrationStatus.slice(1).toLowerCase()}
            </span>
          )}
        </div>

        <span className="text-xs text-slate-500 dark:text-slate-400">
          Timeline: {pStart ? moment(pStart).format("MMM DD") : "-"} -{" "}
          {pEnd ? moment(pEnd).format("MMM DD") : "-"}
        </span>
        <div className="text-xs text-slate-500 dark:text-slate-400">
          Progress: {progressVal}%
        </div>
        {showCostVal ? (
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Cost: ${projectData?.project_cost}
          </span>
        ) : null}

        {projectData?.is_sim_pending && (
          <div className="text-[11px] text-[#467966] dark:text-blue-400 inline-flex items-center gap-1">
            <FaGlasses className="text-[#467966]" />
            {`Simulation changes are available`}
          </div>
        )}

        {projectId ? (
          <div className="flex items-center justify-between w-full group border-t border-slate-200 dark:border-slate-700 !mt-3 pt-1">
            <span
              className=" text-slate-500 dark:text-slate-400 text-left pr-2 flex-1 cursor-pointer hover:text-slate-700 dark:hover:text-slate-200 text-[9px] font-bold"
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(String(projectId), "Project ID");
              }}
            >
              ID: {projectId}
            </span>
            <button
              className={`ml-2 p-1 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0 ${copiedKey === "Project ID" ? "text-green-500" : "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"}`}
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(String(projectId), "Project ID");
              }}
            >
              <CopyIcon isCopied={copiedKey === "Project ID"} />
            </button>
          </div>
        ) : null}

        {(() => {
          const integrationUrl =
            projectData?.integration_data?.integration_url ||
            projectData?.integration_data?.integration_tags?.integration_url;
          return (
            integrationUrl && (
              <a
                href={integrationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
                Open in{" "}
                {integrationUrl.includes("azure") ||
                integrationUrl.includes("visualstudio")
                  ? "Azure DevOps"
                  : "Jira"}
              </a>
            )
          );
        })()}
      </div>
    );
  } else if (tooltipType === 'task' && taskData) {
      const tStart = taskData?.start;
      const tEnd = taskData?.end;
      const tStatusKey = (taskData?.status || '').toLowerCase();
      const tStatusLabel = getDashboardStatusLabel(tStatusKey);
      const tStatusHexColor = dashboardStatusHexColors[tStatusKey] || '#a3a3a3';
      const tShowRiskTriangle = tStatusKey === DashboardStatus.DELAYED || tStatusKey === DashboardStatus.UNALLOCATED || tStatusKey === DashboardStatus.BLOCKED;
      const taskName = taskData?.task_title || taskData?.name;
      const taskId = taskData?.item_id || taskData?.id;

      const tEntityInfo = getEntityTypeDisplay('task', undefined);
      const TIcon = tEntityInfo.icon;

      return (
        <div className="p-3 space-y-1 text-slate-700 dark:text-slate-200 flex flex-col items-start min-w-[220px]">
          {!isTemplate && tShowRiskTriangle && (
            <div className="text-[11px] text-red-600 dark:text-red-400 inline-flex items-center gap-1">
              <FiAlertTriangle className="text-red-500" />
              {`This is contributing to risk`}
            </div>
          )}

          <div className="flex flex-col w-full gap-2 pb-2 border-b border-slate-200 dark:border-slate-700">
             <div className="flex items-center justify-between w-full group">
              <span className="text-sm font-bold text-start flex-1 pr-2 cursor-pointer hover:text-slate-800 dark:hover:text-slate-100" onClick={(e) => { e.stopPropagation(); copyToClipboard(taskName, 'Task Name'); }}>{taskName}</span>
              <button className={`p-1 transition-colors opacity-0 group-hover:opacity-100 ${copiedKey === 'Task Name' ? 'text-green-500' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`} onClick={(e) => { e.stopPropagation(); copyToClipboard(taskName, 'Task Name'); }}>
                <CopyIcon isCopied={copiedKey === 'Task Name'} />
              </button>
            </div>

            <div className={`${tEntityInfo.chipClass} px-2 py-1 flex items-center justify-center bg-gray-200 rounded-full w-fit gap-2`}>
              {TIcon && <TIcon className={`w-3 h-3 ${tEntityInfo.iconClass}`} />}
              <span className={`${tEntityInfo.labelClass} text-[10px]`}>
                {tEntityInfo.label}
                {taskData?.integration_data?.integration_type
                  ? <span className='uppercase'>{" "}({formatTooltipText(taskData.integration_data.integration_type)})</span>
                  : ""}
              </span>
            </div>
         </div>

          <div className="flex items-center w-full ml-[-5px] gap-1 flex-wrap pb-2">
            {!isTemplate && (
              <span className="text-[11px] text-slate-500 dark:text-slate-400 uppercase tracking-wide px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800">
                Status:{" "}
                <b style={{ color: tStatusHexColor }}>{tStatusLabel || "-"}</b>
              </span>
            )}
            {taskData?.priority && (
              <span className="text-[11px] text-slate-500 dark:text-slate-400 uppercase tracking-wide px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800">
                Priority: {taskData.priority}
              </span>
            )}
            {taskData?.integration_data?.integration_status && taskData.integration_data.integration_status.toLowerCase() !== tStatusKey && (
              <span className="text-[11px] text-slate-500 dark:text-slate-400 uppercase tracking-wide px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800">
                Integration Status:{" "}
                {taskData.integration_data.integration_status.charAt(0).toUpperCase() + taskData.integration_data.integration_status.slice(1).toLowerCase()}
              </span>
            )}
            {taskData?.integration_data?.integration_tags?.board_column && (
               <span className="text-[11px] text-slate-500 dark:text-slate-400 uppercase tracking-wide px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800">
                Board Column: <span className="font-medium text-slate-600 dark:text-slate-300">{taskData.integration_data.integration_tags.board_column}</span>
              </span>
            )}
          </div>
          
          {/* Story Points - Show for user stories, bugs, and spikes */}
          {['user story', 'bug', 'spike'].includes(taskData?.integration_data?.integration_item_type?.toLowerCase() || '') &&
            taskData?.integration_data?.integration_tags?.story_points && (
              <span className="text-xs text-slate-500 dark:text-slate-400 inline-flex items-center gap-1">
                Story Points: {taskData.integration_data.integration_tags.story_points}
                {taskData?.integration_data?.integration_tags?.story_points_computed && (
                  <CustomTooltip title="Story points computed by Cadence Pro" placement="top" arrow>
                    <span className="text-yellow-500 dark:text-yellow-400 cursor-help">★</span>
                  </CustomTooltip>
                )}
                {taskData?.integration_data?.integration_tags?.completed_story_points && (
                  <>
                    <span className="text-slate-400 dark:text-slate-500">|</span>
                    <span>Completed: {taskData.integration_data.integration_tags.completed_story_points}</span>
                    {taskData?.integration_data?.integration_tags?.completed_story_points_computed ? (
                      <CustomTooltip title="Actual Story Points computed by Cadence Pro" placement="top" arrow>
                        <span className="text-yellow-500 dark:text-yellow-400 cursor-help">★</span>
                      </CustomTooltip>
                    ) : (
                      <CustomTooltip title="Actual Story Points in Azure DevOps" placement="top" arrow>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-3 h-3 text-slate-400 dark:text-slate-500 cursor-help hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="12" cy="12" r="10"></circle>
                          <path d="M12 16v-4"></path>
                          <path d="M12 8h.01"></path>
                        </svg>
                      </CustomTooltip>
                    )}
                  </>
                )}
              </span>
            )}
          {/* Estimate - Show for task type */}
          {taskData?.integration_data?.integration_item_type?.toLowerCase() === 'task' &&
            taskData?.integration_data?.integration_tags?.original_estimate && (
              <span className="text-xs text-slate-500 dark:text-slate-400 inline-flex items-center gap-1">
                Original Estimate: {taskData.integration_data.integration_tags.original_estimate}
                {taskData?.integration_data?.integration_tags?.original_estimate_computed && (
                  <CustomTooltip title="Estimate computed by Cadence Pro" placement="top" arrow>
                    <span className="text-yellow-500 dark:text-yellow-400 cursor-help">★</span>
                  </CustomTooltip>
                )}
                {taskData?.integration_data?.integration_tags?.completed_original_estimate && (
                  <>
                    <span className="text-slate-400 dark:text-slate-500">|</span>
                    <span>Completed: {taskData.integration_data.integration_tags.completed_original_estimate}</span>
                    {taskData?.integration_data?.integration_tags?.completed_original_estimate_computed ? (
                      <CustomTooltip title="Completed Estimate computed by Cadence Pro" placement="top" arrow>
                        <span className="text-yellow-500 dark:text-yellow-400 cursor-help">★</span>
                      </CustomTooltip>
                    ) : (
                      <CustomTooltip title="Completed Estimate in Azure DevOps" placement="top" arrow>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-3 h-3 text-slate-400 dark:text-slate-500 cursor-help hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="12" cy="12" r="10"></circle>
                          <path d="M12 16v-4"></path>
                          <path d="M12 8h.01"></path>
                        </svg>
                      </CustomTooltip>
                    )}
                  </>
                )}
              </span>
            )}
          
          <span className="text-xs text-slate-500 dark:text-slate-400">Timeline: {tStart ? moment(tStart).format('MMM DD') : '-'} - {tEnd ? moment(tEnd).format('MMM DD') : '-'}</span>
          <span className="text-xs text-slate-500 dark:text-slate-400">Progress: {Math.floor(Number(taskData?.progress || 0))}%</span>
          {/* Assigned To */}
          {(() => {
            const responsibleId = taskData?.responsible || taskData?.responsible_id || taskData?.integration_data?.responsible_id;
            const assignedName = taskData?.member?.name || (responsibleId ? getResourceName(responsibleId) : null);
            return assignedName && assignedName !== '-' ? (
              <div className="text-xs text-slate-500 dark:text-slate-400 w-full flex items-center gap-2">
               <span className='text-left'>Assigned to:</span> <span className='text-blue-500 max-w-[80%] overflow-hidden text-ellipsis'>{assignedName}</span>
              </div>
            ) : null;
          })()}
          {/* Integration Item Type - at the end */}
          {taskData?.integration_data?.integration_item_type && (
            <span className="text-xs text-slate-500 dark:text-slate-400 capitalize">
              Integration Type: {taskData.integration_data.integration_item_type}
            </span>
          )}

          {/* Integration URL - Clickable link to Azure/Jira */}
          {(() => {
            const integrationUrl = taskData?.integration_data?.integration_url || taskData?.integration_data?.integration_tags?.integration_url;
            return integrationUrl && (
              <a
                href={integrationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Open in {integrationUrl.includes('azure') || integrationUrl.includes('visualstudio') ? 'Azure DevOps' : 'Jira'}
              </a>
            );
          })()}

          {/* Work Item ID - at the bottom like Project ID */}
          {taskId ? (
            <div className="flex items-center justify-between w-full group border-t border-slate-200 dark:border-slate-700 !mt-3 pt-1">
              <span className="text-slate-500 dark:text-slate-400 text-left pr-2 flex-1 cursor-pointer hover:text-slate-700 dark:hover:text-slate-200 text-[9px] font-bold" onClick={(e) => { e.stopPropagation(); copyToClipboard(String(taskId), 'Task ID'); }}>Work Item ID: {taskId}</span>
              <button className={`ml-2 p-1 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0 ${copiedKey === 'Task ID' ? 'text-green-500' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`} onClick={(e) => { e.stopPropagation(); copyToClipboard(String(taskId), 'Task ID'); }}>
                <CopyIcon isCopied={copiedKey === 'Task ID'} />
              </button>
            </div>
          ) : null}
        </div>
      );
  }
  
  return null;
};
